import type { QueryClient, QueryKey } from "@tanstack/react-query";

export const PROGRAM_QUERY_GC_TIME = 1000 * 60 * 60 * 12;

export const PROGRAM_QUERY_STALE_TIMES = {
  programsInfo: 1000 * 60 * 15,
  countries: 1000 * 60 * 60,
  folders: 1000 * 60 * 10,
  indicators: 1000 * 60 * 15,
  reporting: 1000 * 60 * 5,
  reference: 1000 * 60 * 60,
} as const;

export const programQueryKeys = {
  programsInfo: "programsInfo",
  programInfo: (programId: number | string) =>
    ["programInfo", String(programId)] as const,
  programsOwners: "programsOwners",
  countries: "countriesList",
  devises: "devises",
  carrierFolders: "programCarrierFolders",
  folders: (programId: number | string) =>
    ["programFolders", String(programId)] as const,
  indicators: (programId: number | string, userId: number | string) =>
    ["programIndicators", String(programId), String(userId)] as const,
  indicatorReporting: (
    programId: number | string,
    indicatorId: number | string,
    projectsKey: string,
  ) =>
    [
      "programIndicatorReporting",
      String(programId),
      String(indicatorId),
      projectsKey || "all",
    ] as const,
  reporting: (programId: number | string, projectsKey: string) =>
    ["programReporting", String(programId), projectsKey || "all"] as const,
} as const;

const PROGRAM_PERSISTED_QUERY_KEY_ROOTS = new Set([
  programQueryKeys.programsInfo,
  programQueryKeys.programsOwners,
  programQueryKeys.countries,
  programQueryKeys.devises,
  programQueryKeys.carrierFolders,
  "programInfo",
  "programFolders",
  "programIndicators",
  "programIndicatorReporting",
  "programReporting",
]);

export function isProgramPersistedQueryKey(queryKey: QueryKey) {
  if (!Array.isArray(queryKey) || typeof queryKey[0] !== "string") {
    return false;
  }

  return PROGRAM_PERSISTED_QUERY_KEY_ROOTS.has(queryKey[0]);
}

export function updateProgramInProgramsInfoCache(
  queryClient: QueryClient,
  programId: number | string | undefined,
  patch: Record<string, any>,
) {
  const normalizedProgramId = Number(programId || 0);
  if (!normalizedProgramId) return;

  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    },
    (current: unknown) => {
      if (!Array.isArray(current)) return current;

      let hasChanged = false;
      const nextPrograms = current.map((program: any) => {
        if (Number(program?.id || 0) !== normalizedProgramId) {
          return program;
        }

        hasChanged = true;
        return {
          ...program,
          ...patch,
        };
      });

      return hasChanged ? nextPrograms : current;
    },
  );
  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: programQueryKeys.programInfo(normalizedProgramId),
    },
    (current: unknown) => {
      if (!Array.isArray(current)) return current;

      let hasChanged = false;
      const nextPrograms = current.map((program: any) => {
        if (Number(program?.id || 0) !== normalizedProgramId) {
          return program;
        }

        hasChanged = true;
        return {
          ...program,
          ...patch,
        };
      });

      return hasChanged ? nextPrograms : current;
    },
  );
}

export function upsertProgramProjectInProgramsInfoCache(
  queryClient: QueryClient,
  programId: number | string | undefined,
  project: Record<string, any>,
  previousProjectId?: number | string,
) {
  const normalizedProgramId = Number(programId || 0);
  const normalizedProjectId = Number(project?.id || 0);
  const normalizedPreviousProjectId = Number(previousProjectId || 0);
  if (!normalizedProgramId || !normalizedProjectId) return;

  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    },
    (current: unknown) => {
      if (!Array.isArray(current)) return current;

      let hasChanged = false;
      const nextPrograms = current.map((program: any) => {
        if (Number(program?.id || 0) !== normalizedProgramId) {
          return program;
        }

        const projects = Array.isArray(program?.projects)
          ? program.projects
          : [];
        const existingIndex = projects.findIndex((item: any) => {
          const itemId = Number(item?.id || 0);
          return (
            itemId === normalizedProjectId ||
            (normalizedPreviousProjectId && itemId === normalizedPreviousProjectId)
          );
        });
        const nextProjects =
          existingIndex >= 0
            ? projects.map((item: any, index: number) =>
                index === existingIndex ? { ...item, ...project } : item,
              )
            : [...projects, project];

        hasChanged = true;
        return {
          ...program,
          projects: nextProjects,
        };
      });

      return hasChanged ? nextPrograms : current;
    },
  );
  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: programQueryKeys.programInfo(normalizedProgramId),
    },
    (current: unknown) => {
      if (!Array.isArray(current)) return current;

      let hasChanged = false;
      const nextPrograms = current.map((program: any) => {
        if (Number(program?.id || 0) !== normalizedProgramId) {
          return program;
        }

        const projects = Array.isArray(program?.projects)
          ? program.projects
          : [];
        const existingIndex = projects.findIndex((item: any) => {
          const itemId = Number(item?.id || 0);
          return (
            itemId === normalizedProjectId ||
            (normalizedPreviousProjectId && itemId === normalizedPreviousProjectId)
          );
        });
        const nextProjects =
          existingIndex >= 0
            ? projects.map((item: any, index: number) =>
                index === existingIndex ? { ...item, ...project } : item,
              )
            : [...projects, project];

        hasChanged = true;
        return {
          ...program,
          projects: nextProjects,
        };
      });

      return hasChanged ? nextPrograms : current;
    },
  );
}

export function removeProgramProjectFromProgramsInfoCache(
  queryClient: QueryClient,
  programId: number | string | undefined,
  projectId: number | string | undefined,
) {
  const normalizedProgramId = Number(programId || 0);
  const normalizedProjectId = Number(projectId || 0);
  if (!normalizedProgramId || !normalizedProjectId) return;

  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    },
    (current: unknown) => {
      if (!Array.isArray(current)) return current;

      let hasChanged = false;
      const nextPrograms = current.map((program: any) => {
        if (Number(program?.id || 0) !== normalizedProgramId) {
          return program;
        }

        const projects = Array.isArray(program?.projects)
          ? program.projects
          : [];
        const nextProjects = projects.filter(
          (item: any) => Number(item?.id || 0) !== normalizedProjectId,
        );
        if (nextProjects.length === projects.length) {
          return program;
        }

        hasChanged = true;
        return {
          ...program,
          projects: nextProjects,
        };
      });

      return hasChanged ? nextPrograms : current;
    },
  );
  queryClient.setQueriesData(
    {
      exact: false,
      queryKey: programQueryKeys.programInfo(normalizedProgramId),
    },
    (current: unknown) => {
      if (!Array.isArray(current)) return current;

      let hasChanged = false;
      const nextPrograms = current.map((program: any) => {
        if (Number(program?.id || 0) !== normalizedProgramId) {
          return program;
        }

        const projects = Array.isArray(program?.projects)
          ? program.projects
          : [];
        const nextProjects = projects.filter(
          (item: any) => Number(item?.id || 0) !== normalizedProjectId,
        );
        if (nextProjects.length === projects.length) {
          return program;
        }

        hasChanged = true;
        return {
          ...program,
          projects: nextProjects,
        };
      });

      return hasChanged ? nextPrograms : current;
    },
  );
}
